import { observer } from 'mobx-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useRaptorComponentsStore } from 'store';
import { useUrlSearch } from 'hooks/use-search.hook';
import { ComponentVersionStatus } from 'dto/raptor-component-version.dto';
import { ComponentType } from 'dto/raptor-component.dto';
import { DockerImageDto } from 'dto/raptor-docker-image.dto';
import { notification, Skeleton } from 'ui-lib';
import {
    buildEmptyComponentVersionFormValues,
    buildUpdateComponentVersionPayload,
    ComponentVersionDisplayHelpers,
} from './component-version.helpers';
import { ComponentVersionWizard } from './component-version-wizard';
import { ComponentVersionEmptyState } from './component-version-empty-state';
import { useComponentVersionForm } from './hooks/use-component-version-form';
import { IComponentVersionFormValues } from './component-version-wizard.types';
import { useRaptorComponentNavigator } from '../../hooks/use-raptor-component-navigator';

export const EditComponentVersionForm = observer(() => {
    const { componentUuid, versionUuid } = useParams();
    const { getSearchParam } = useUrlSearch();
    const componentsStore = useRaptorComponentsStore();
    const accountUuid = getSearchParam('accountUuid');
    const { account, component, componentVersion, loaded, loadingHook } = useComponentVersionForm({
        accountUuid,
        componentUuid,
        componentVersionUuid: versionUuid,
    });
    const navigator = useRaptorComponentNavigator();
    const toComponentListingPage = () => navigator.toComponentListingPage({ accountUuid: account?.uuid });
    const initialValues = React.useMemo(() => {
        if (!component || !componentVersion) {
            return buildEmptyComponentVersionFormValues();
        }

        const manifests = componentVersion.manifests.reduce(
            (result, manifest) => {
                result[manifest.platformUuid] = {
                    lifecycle: manifest.lifecycle?.raw,
                    uploadedFiles: manifest.uploadedFiles,
                    dockerImages: manifest.dockerImages.map((dockerImage) => new DockerImageDto(dockerImage.raw)),
                };
                return result;
            },
            {} as NonNullable<IComponentVersionFormValues['manifests']>,
        );

        const values: IComponentVersionFormValues = {
            version: componentVersion.version,
            description: componentVersion.description || undefined,
            platformUuids: componentVersion.manifests.map((manifest) => manifest.platformUuid),
            manifests,
        };

        if (component.type === ComponentType.CATAPULT) {
            values.configurationJson = JSON.stringify(componentVersion.configuration || {}, null, 2);
        }

        return values;
    }, [component, componentVersion]);

    if (loadingHook.loading || !loaded) {
        return <Skeleton loading={true} />;
    }

    if (!account || !component || !componentVersion) {
        return (
            <ComponentVersionEmptyState
                title='Component version cannot be edited'
                description='The selected account, component, or component version could not be found.'
                actionLabel='Back to components'
                onAction={() => toComponentListingPage()}
            />
        );
    }

    if (componentVersion.status === ComponentVersionStatus.PUBLISHED) {
        return (
            <ComponentVersionEmptyState
                title='Component version cannot be edited'
                description='The selected component version is published.'
                actionLabel='Back to components'
                onAction={() => toComponentListingPage()}
            />
        );
    }

    return (
        <ComponentVersionWizard
            title={`Edit Version for ${component.displayName}`}
            versionDisabled={true}
            account={account}
            component={component}
            initialValues={initialValues}
            onSubmit={async (values) => {
                const updatePayload = buildUpdateComponentVersionPayload(component.type, values);
                const componentVersionDisplayName = ComponentVersionDisplayHelpers.getComponentVersionDisplayName(
                    component.displayName,
                    componentVersion.version,
                );
                const result = await loadingHook.withSaving(async () => {
                    return componentsStore.updateComponentVersion(account.id, componentVersion.uuid, updatePayload);
                });

                if (result.isSuccess()) {
                    notification.success({
                        message: `Component ${componentVersionDisplayName} updated successfully`,
                    });
                    return toComponentListingPage();
                }

                notification.error({
                    message: `Failed to update component ${componentVersionDisplayName}`,
                    description: result.errorMessage,
                });
            }}
            onCancel={() => toComponentListingPage()}
            loading={loadingHook.loading}
            saving={loadingHook.saving}
            errorMessages={loadingHook.errorMessages}
        />
    );
});
